/* NaturoElite Landing Page System v2 (NE2)
   -----------------------------------------------------------------------
   Presentation, plus one cart path:

   1. Variant switching inside snippets/ne2-buy-form.liquid. The radios are not
      named "id" — the theme's ProductForm reads the first [name=id] it finds
      and force-enables it, which would resurrect a sold-out radio. A hidden
      [name=id] carries the choice instead and the radios write to it. A pick
      in one switcher updates every buy for that product on the page.

   2. Placing and revealing the sticky buy bar, which docks under the theme
      header rather than at the foot of the window.

   3. The anchor links inside that bar: the mobile drawer, and lighting the
      link whose section the reader is currently in.

   4. The FAQ accordion: one answer open at a time, animated.

   5. Add to cart for the formless buy buttons. Only NE2 Product box renders a
      real <form>; the hero and the sticky bar add over the theme's JSON cart
      route instead. The reason is written up in snippets/ne2-buy-form.liquid.

   Adding to cart itself is the theme's <form is="product-form">, so the cart
   drawer, the cart count and the 'product-ajax:added' event all behave exactly
   as they do on a product page. Nothing here touches that.

   Runs on DOMContentLoaded and again on shopify:section:load, so sections
   re-rendered by the theme editor rewire themselves. */
(function () {
  'use strict';

  /* A size picked in any switcher is applied to every buy for the same
     product on the page (hero, product box, CTA band, sticky bar). The picked
     radio's data attributes supply the price, title and availability. */
  var chosenVariant = {};

  function applyVariant(root, radio) {
    var idInput = root.querySelector('[data-ne2-id]');
    var button = root.querySelector('[data-ne2-submit]');
    var price = root.querySelector('[data-ne2-price]');
    var compare = root.querySelector('[data-ne2-compare]');

    if (idInput && idInput.value !== radio.value) {
      idInput.value = radio.value;
      // Product box form only: lets Bundler / Seal Subscriptions see the change.
      if (idInput.name === 'id') {
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Keep this box's own switcher, if it has one, showing the same pick.
    var own = root.querySelector('[data-ne2-variant][value="' + radio.value + '"]');
    if (own && !own.checked) own.checked = true;

    if (price && radio.dataset.ne2PriceText) {
      price.textContent = radio.dataset.ne2PriceText;
    }
    if (compare) {
      compare.textContent = radio.dataset.ne2CompareText || '';
      compare.hidden = !radio.dataset.ne2CompareText;
    }
    root.querySelectorAll('[data-ne2-size]').forEach(function (size) {
      size.textContent = radio.dataset.ne2Title || '';
    });
    if (button) {
      var available = radio.dataset.ne2Available === 'true';
      /* The formless buy is an <a>, which has no disabled property — setting
         it would silently do nothing and leave a sold-out variant buyable.
         aria-disabled is what the click handler and the CSS both read, and
         the real <button> in the product box honours both. */
      if ('disabled' in button) button.disabled = !available;
      if (available) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }
      button.textContent = available
        ? button.dataset.ne2Label
        : button.dataset.ne2SoldOutLabel;
    }
  }

  function buysFor(productId) {
    return document.querySelectorAll('[data-ne2-buy][data-ne2-product-id="' + productId + '"]');
  }

  function selectVariant(radio) {
    var root = radio.closest('[data-ne2-buy]');
    if (!root) return;
    var productId = root.dataset.ne2ProductId;
    if (!productId) {
      applyVariant(root, radio);
      return;
    }
    chosenVariant[productId] = radio.value;
    buysFor(productId).forEach(function (buy) { applyVariant(buy, radio); });
  }

  function initBuyForm(root) {
    if (root.dataset.ne2VariantsReady === 'true') return;
    root.dataset.ne2VariantsReady = 'true';

    root.querySelectorAll('[data-ne2-variant]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) selectVariant(radio);
      });
    });

    // A section re-rendered in the editor rejoins the size already picked.
    var chosen = chosenVariant[root.dataset.ne2ProductId];
    var source = chosen && document.querySelector('[data-ne2-variant][value="' + chosen + '"]');
    if (!source) source = root.querySelector('[data-ne2-variant]:checked');
    if (source) applyVariant(root, source);
  }

  /* The bar is docked under the theme header, so it has to know where the
     bottom of that header currently is, and the answer changes constantly:
     .header-section is position:sticky, the announcement bar above it scrolls
     away, and header.js hides .ne-header with translateY(-100%) on scroll down
     and brings it back on scroll up over ~0.3s.

     So measure the header element's live bottom edge each frame and publish it
     as --ne2-header-offset. Everything the bar needs to know about the header
     is in that one number: when the header is off screen the edge is at or
     above 0 and the bar sits flush against the top of the window. */
  function initSticky(bar) {
    if (bar.dataset.ne2Ready === 'true') return;
    bar.dataset.ne2Ready = 'true';

    var docEl = document.documentElement;
    /* A permanently visible bar covers the top of whatever is under it, so the
       page content has to start below it. This used to be a spacer <div> next
       to the bar, which only worked while this section was first in the
       template — and it can no longer be first, because the first
       add-to-cart form on the page is the one discount apps inject into. See
       the note in sections/ne2-sticky-buy.liquid. Padding on the layout's own
       content element does the same job from anywhere in the order. */
    var contentHost = document.getElementById('MainContent');
    var alwaysOn = bar.dataset.ne2Reveal !== 'after-hero';
    var trigger = document.querySelector('[data-ne2-sticky-trigger]');

    // The header element itself, not its .header-section wrapper: the wrapper
    // stays stuck at top:0 while header.js translates the header inside it out
    // of view, so the wrapper's bottom edge never reports the header as hidden.
    var themeHeader = document.querySelector('.ne-header') || document.querySelector('.header');

    function headerBottom() {
      if (!themeHeader) return 0;
      var bottom = themeHeader.getBoundingClientRect().bottom;
      return bottom > 0 ? Math.round(bottom) : 0;
    }

    var lastOffset = null;
    var lastHeaderH = null;
    var lastStickyH = null;
    var lastPad = null;
    var ticking = false;
    var followUntil = 0;

    function update() {
      var offset = headerBottom() + 'px';
      if (offset !== lastOffset) {
        docEl.style.setProperty('--ne2-header-offset', offset);
        lastOffset = offset;
      }

      /* The header's own height, which is what it occupies once it is stuck —
         not its live bottom edge, which is larger while the announcement bar is
         still on screen. Only scroll-margin-top reads this; the bar's own top
         still follows the live edge above. */
      var headerH = (themeHeader ? themeHeader.offsetHeight : 0) + 'px';
      if (headerH !== lastHeaderH) {
        docEl.style.setProperty('--ne2-anchor-offset', headerH);
        lastHeaderH = headerH;
      }

      // With no hero on the page there is nothing to scroll past, so the bar
      // stays on — see the note in sections/ne2-sticky-buy.liquid.
      var visible = alwaysOn || !trigger
        ? true
        : trigger.getBoundingClientRect().bottom <= headerBottom();
      bar.classList[visible ? 'add' : 'remove']('is-visible');

      // Only a permanently visible bar needs the page pushed down past it. One
      // revealed on scroll appears over content that is already moving.
      var height = bar.offsetHeight;
      var pad = alwaysOn ? height + 'px' : '';
      if (contentHost && pad !== lastPad) {
        contentHost.style.paddingTop = pad;
        lastPad = pad;
      }

      /* Read by scroll-margin-top on .ne2-section so an anchor link lands below
         the bar. Measured FROM the bar, so nothing that sizes the bar may read
         it back — see the min-height note on .ne2-sticky__inner. */
      var stickyH = height + 'px';
      if (stickyH !== lastStickyH) {
        docEl.style.setProperty('--ne2-sticky-h', stickyH);
        lastStickyH = stickyH;
      }
    }

    /* Keep sampling for a beat after the last scroll event.

       The header slides in and out over --animation-default (~0.3s), and scroll
       events stop the moment the reader does. Sampling on scroll alone left the
       bar frozen part-way through that slide, then snapping into place on the
       next scroll. Following for 600ms covers the whole animation; every write
       above is guarded on change, so the extra frames cost two layout reads. */
    function follow() {
      update();
      if (Date.now() < followUntil) {
        window.requestAnimationFrame(follow);
        return;
      }
      ticking = false;
    }

    function schedule() {
      followUntil = Date.now() + 600;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(follow);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    update();
    // header.js sets its own height asynchronously (delayUntilInteraction), and
    // web fonts reflow the header after that, so re-measure once things settle.
    window.setTimeout(update, 300);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update);
    }
    if (typeof ResizeObserver === 'function' && themeHeader) {
      new ResizeObserver(schedule).observe(themeHeader);
    }
  }

  /* Add to cart for every formless buy on the page — the hero and the sticky
     bar. Only NE2 Product box renders a real form, and that one is the theme's
     <form is="product-form"> and never reaches this code.

     This is the theme's own formless path, lifted from the product-bundles
     component in assets/theme.js: POST the items to FoxTheme.routes.cart_add_url,
     re-read the cart, publish cartUpdate, fire 'product-ajax:added', open the
     drawer. Doing it by hand rather than through a form leaves nothing on the
     page for a discount or subscription app to inject into, which is the whole
     reason these buttons look like this — see snippets/ne2-buy-form.liquid.

     Everything downstream is untouched: the cart drawer opens, the cart count
     refreshes, and apps listening for 'product-ajax:added' still hear it. */
  function initAdd(root) {
    var button = root.querySelector('[data-ne2-submit]');
    if (!button || button.dataset.ne2Ready === 'true') return;
    button.dataset.ne2Ready = 'true';

    // Written by the variant switcher when there is one, so the id is read at
    // click time rather than captured now.
    var idInput = root.querySelector('[data-ne2-id]');
    var errorEl = root.querySelector('.ne2-buy__error');
    if (!idInput) return;

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function release() {
      button.classList.remove('btn--loading');
      button.removeAttribute('aria-disabled');
    }

    button.addEventListener('click', function (event) {
      // Let a middle click or a cmd/ctrl click open the product page, the way
      // any other link on the page would.
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      // If the theme's JS has not loaded, the href is the honest fallback.
      var api = window.FoxTheme;
      if (!api || !api.routes || !api.utils || !api.pubsub) return;

      event.preventDefault();
      if (button.getAttribute('aria-disabled') === 'true') return;

      var variantId = idInput.value;
      if (!variantId) return;
      var items = [{ id: variantId, quantity: 1 }];

      /* On the cart page, and whenever the drawer is switched off in theme
         settings, the theme posts a real form and lets the browser navigate —
         there is no drawer to open. Match that rather than silently doing
         nothing. */
      if (
        document.body.classList.contains('cart-template') ||
        (api.settings && api.settings.cartType !== 'drawer')
      ) {
        api.utils.postLink(api.routes.cart_add_url, { parameters: { items: items } });
        return;
      }

      showError('');
      button.classList.add('btn--loading');
      button.setAttribute('aria-disabled', 'true');

      /* Sections that want re-rendering with the new cart announce themselves
         on this event; the cart drawer is one of them. Collecting them here is
         what keeps the drawer's contents in step. */
      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
      );

      var config = api.utils.fetchConfig('javascript');
      config.body = JSON.stringify({
        items: items,
        sections: sections,
        section_url: window.location.pathname
      });

      fetch(api.routes.cart_add_url, config)
        .then(function (response) { return response.json(); })
        .then(function (added) {
          // A 4xx from /cart/add comes back as 200 with a status field on it.
          if (added.status) {
            showError(added.description || added.message);
            release();
            return;
          }

          return fetch(api.routes.cart_url, api.utils.fetchConfig())
            .then(function (response) { return response.json(); })
            .then(function (cart) {
              cart.sections = added.sections;
              api.pubsub.publish(api.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
              document.dispatchEvent(
                new CustomEvent('product-ajax:added', { detail: { product: added } })
              );

              var drawer = document.querySelector('cart-drawer');
              if (drawer && drawer.show) drawer.show(button);
              release();
            });
        })
        .catch(function () {
          showError('Could not add to cart. Please try again.');
          release();
        });
    });
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* How far below the top of the window an anchored section has to land.

     Measured at click time from the elements themselves, and deliberately from
     the header's HEIGHT rather than its current bottom edge. Those are two
     different numbers: at the top of the page the announcement bar is still on
     screen and pushes the header's bottom edge down, but by the time the scroll
     finishes the announcement has gone and the header is stuck at top: 0. Using
     the edge meant every anchor clicked from near the top of the page
     overshot by the height of the announcement bar and left a strip of the
     previous section showing under the buy bar.

     No breathing gap on top: every NE2 section carries its own top padding, and
     because the sections are full-bleed colours, a gap shows as a slice of the
     section above rather than as space. */
  function anchorOffset() {
    var header = document.querySelector('.ne-header') || document.querySelector('.header');
    // Only a bar that is actually on screen takes up room.
    var bar = document.querySelector('[data-ne2-sticky].is-visible');
    return (header ? header.offsetHeight : 0) + (bar ? bar.offsetHeight : 0);
  }

  /* Above 750px the anchor links are a row inside the bar and the toggle is
     display:none, so every listener below is inert there — no matchMedia
     needed, the CSS decides.

     Opening only flips a class. Scrolling to the section stays initAnchorNav's
     job: the drawer holds the same <a href="#..."> elements it already binds. */
  function initStickyNavToggle(nav) {
    var toggle = nav.querySelector('[data-ne2-nav-toggle]');
    if (!toggle || toggle.dataset.ne2Ready === 'true') return;
    toggle.dataset.ne2Ready = 'true';

    var closeButton = nav.querySelector('[data-ne2-nav-close]');
    var openedAtY = 0;

    function close() {
      if (!nav.classList.contains('is-open')) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function (event) {
      // Without this the document listener below sees the same click and
      // closes the drawer in the same tick it was opened.
      event.stopPropagation();
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      openedAtY = window.scrollY;
      // Move into the drawer rather than leaving focus on the icon behind it.
      if (open && closeButton) closeButton.focus();
    });

    /* Scrolling the page puts the drawer away.

       Against a threshold rather than on any scroll event at all: iOS fires
       these as the address bar collapses and as momentum from the tap that
       opened the drawer settles, and either would shut it in the same breath
       it opened. 40px is past both and well short of a deliberate swipe. */
    window.addEventListener(
      'scroll',
      function () {
        if (!nav.classList.contains('is-open')) return;
        if (Math.abs(window.scrollY - openedAtY) < 40) return;
        close();
      },
      { passive: true }
    );

    /* Three ways out from inside the drawer: pick a section, hit Close, or tap
       the ground beside it. The ground is a child of the nav, so the document
       listener below never sees it as an outside click. */
    nav.addEventListener('click', function (event) {
      if (
        event.target.closest('a') ||
        event.target.closest('[data-ne2-nav-close]') ||
        event.target.closest('[data-ne2-nav-scrim]')
      ) {
        close();
      }
    });

    document.addEventListener('click', function (event) {
      if (!nav.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !nav.classList.contains('is-open')) return;
      close();
      // Escape must not strand focus inside a panel that is now hidden.
      toggle.focus();
    });
  }

  function initAnchorNav(nav) {
    if (nav.dataset.ne2Ready === 'true') return;
    nav.dataset.ne2Ready = 'true';

    initStickyNavToggle(nav);

    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    var targets = links
      .map(function (link) {
        var id = link.getAttribute('href').slice(1);
        var el = id ? document.getElementById(id) : null;
        return el ? { link: link, el: el } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    /* Scroll without leaving a fragment in the address bar.

       The markup stays a real <a href="#...">, so the links are still keyboard
       reachable, still open in a new tab on a modified click, and still work
       with JavaScript off. This only takes over the plain left click.

       Why bother: every click would otherwise write a hash, and the browser
       restores that hash on the next refresh — so reloading the page jumps the
       reader back to whichever section they last clicked.

       The destination is worked out here rather than left to scrollIntoView and
       scroll-margin-top. The browser reads that property once, at the moment the
       scroll starts, so it gets whatever --ne2-header-offset happened to hold
       then — a stale number if the header was still sitting under the
       announcement bar. anchorOffset() measures the elements at click time
       instead. scroll-margin-top stays in the CSS for the plain #hash case,
       where there is no JavaScript in the loop at all. */
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var match = targets.filter(function (t) { return t.link === link; })[0];
        if (!match) return;

        event.preventDefault();

        var top = window.scrollY + match.el.getBoundingClientRect().top - anchorOffset();
        window.scrollTo({
          top: Math.max(0, Math.round(top)),
          behavior: reducedMotion() ? 'auto' : 'smooth'
        });

        if (window.history && history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      });
    });

    if (!('IntersectionObserver' in window)) return;

    /* Track which sections are in view rather than reacting to the last one
       that entered. An add-only version leaves whatever was lit last still lit
       anywhere no target is intersecting — above the first one, in the hero. */
    var inView = [];

    function paint() {
      var current = null;

      // Topmost section in view wins, so scrolling back up hands the highlight
      // over in the order the reader actually sees them.
      targets.forEach(function (t) {
        if (inView.indexOf(t.el) === -1) return;
        if (!current || t.el.getBoundingClientRect().top < current.el.getBoundingClientRect().top) {
          current = t;
        }
      });

      links.forEach(function (l) {
        l.classList[current && l === current.link ? 'add' : 'remove']('is-active');
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = inView.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) inView.push(entry.target);
          if (!entry.isIntersecting && i !== -1) inView.splice(i, 1);
        });
        paint();
      },
      // Light a link when its section crosses the upper third of the screen.
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );

    targets.forEach(function (t) { observer.observe(t.el); });
  }

  /* FAQ accordion — one answer open at a time, ported from nb2-landing.js so
     the two landing systems behave the same way.

     <details> has no height to animate between; it jumps from summary-only to
     full. So the click is intercepted and the height is driven here, which also
     makes closing a sibling a real transition rather than a snap.

     The markup carries the native exclusive `name` attribute as well. That is
     the no-JavaScript path — the browser closes the siblings on its own — and
     this drops the attribute once it takes over, otherwise the browser would
     slam them shut mid-animation. */

  var FAQ_DURATION = 260;
  var FAQ_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  /* Height with the answer hidden: the summary plus the item's own borders,
     which offsetHeight counts and clientHeight does not. */
  function faqClosedHeight(item, summary) {
    return summary.offsetHeight + item.offsetHeight - item.clientHeight;
  }

  function faqStop(item) {
    if (!item.ne2Animation) return;
    item.ne2Animation.cancel();
    item.ne2Animation = null;
  }

  function faqSlide(item, from, to, onDone) {
    item.style.overflow = 'hidden';

    var animation = item.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: FAQ_DURATION, easing: FAQ_EASING }
    );
    item.ne2Animation = animation;

    animation.onfinish = function () {
      item.ne2Animation = null;
      item.style.overflow = '';
      if (onDone) onDone();
    };
  }

  function faqClose(item, summary) {
    // Read before stopping: cancelling drops the animated height.
    var from = item.offsetHeight;
    faqStop(item);

    if (!summary || reducedMotion()) {
      item.removeAttribute('data-ne2-closing');
      item.open = false;
      return;
    }

    // Drives the + back round now rather than when the slide lands.
    item.setAttribute('data-ne2-closing', '');

    faqSlide(item, from, faqClosedHeight(item, summary), function () {
      item.removeAttribute('data-ne2-closing');
      item.open = false;
    });
  }

  function faqOpen(item, list) {
    var others = list.querySelectorAll('details[open]');
    Array.prototype.forEach.call(others, function (other) {
      if (other !== item && !other.hasAttribute('data-ne2-closing')) {
        faqClose(other, other.querySelector('summary'));
      }
    });

    var from = item.offsetHeight;
    faqStop(item);
    item.removeAttribute('data-ne2-closing');
    item.open = true;

    if (reducedMotion()) return;
    faqSlide(item, from, item.offsetHeight);
  }

  function initFaq(list) {
    if (list.dataset.ne2Ready === 'true') return;
    // Without Web Animations the native `name` attribute is left in place and
    // the browser handles the exclusivity by itself, unanimated.
    if (typeof Element.prototype.animate !== 'function') return;
    list.dataset.ne2Ready = 'true';

    list.querySelectorAll('details').forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;

      item.removeAttribute('name');

      summary.addEventListener('click', function (event) {
        event.preventDefault();

        if (item.open && !item.hasAttribute('data-ne2-closing')) {
          faqClose(item, summary);
        } else {
          faqOpen(item, list);
        }
      });
    });
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-ne2-buy]').forEach(initBuyForm);
    root.querySelectorAll('[data-ne2-sticky]').forEach(initSticky);
    root.querySelectorAll('[data-ne2-add]').forEach(initAdd);
    root.querySelectorAll('[data-ne2-nav]').forEach(initAnchorNav);
    root.querySelectorAll('[data-ne2-faq]').forEach(initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
